import React, { FC, useRef, useState } from 'react';
import { Color } from '../Theme';
import GetAppIcon from 'mdi-react/GetAppIcon';
import styled from 'styled-components';
import Cropper from 'react-easy-crop';
import { Crop } from '../../common-react/cropImage';
import { FormattedMessage } from 'react-intl';
import { Help } from '../Help';

const UploadArea = styled.div<{ isDragOnTop: boolean }>`
  margin: 0.8rem 0;

  > h5 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
  }

  > div {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 400px;
    border: ${(props) => (props.isDragOnTop ? 'solid' : 'dashed')} 2px ${(props) => (props.isDragOnTop ? Color.primary : Color.disabled)};
    border-radius: 4px;
    background-color: ${Color.background};
    cursor: pointer;
    user-select: none;
    box-sizing: border-box;

    transition: border 200ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      svg {
        transform: translateY(0.4rem);
        fill: ${Color.primary};
      }
    }

    svg {
      margin: 0 0 2rem 0;
      width: 5rem;
      height: 5rem;
      fill: ${(props) => (props.isDragOnTop ? Color.primary : Color.disabled)};

      transform: ${(props) => (props.isDragOnTop ? 'translateY(0.4rem)' : 'translateY(0)')};

      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    * {
      user-select: none;
      pointer-events: none;
    }
  }

  p {
    margin: 0.4rem 0 0 0;
    font-size: 0.7rem;
    color: ${Color.black};
    opacity: 0.7;
  }

  input {
    display: none;
  }
`;

const CropContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.8rem 0;
  width: 100%;
  height: 400px;
  // background-color: ${Color.background};
  border-radius: 4px;
  overflow: hidden;

  > div {
    position: relative;
    width: 100%;
    height: 100%;
  }
`;

export interface CompanyEditImageProps {
  onComplete: (image: string, crop: Crop | null) => void;
}

export const CompanyEditImage: FC<CompanyEditImageProps> = ({ onComplete }) => {
  const [step, setStep] = useState('select');
  const [isDragOnTop, setIsDragOnTop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [file, setFile] = useState<string>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList) => {
    const showError = (message: string) => {
      // TODO: Implement error
      setIsDragOnTop(false);
    };

    if (!files) return showError('Invalid file selected');
    if (files.length !== 1) return showError('Only one file at a time is allowed');

    const file = files[0];
    if (!file.type.includes('image/')) return showError('Only image files are supported');

    // TODO: We will skip image crop step for now, it was not that intuitive for customers...
    setFile(URL.createObjectURL(file));
    // setStep('crop');

    return onComplete(URL.createObjectURL(file), null);
  };

  const handleUploadFileClick = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const element = event.target;
    if (!element || !element.files || !element.files.length) return;

    handleFileSelect(element.files);
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    setIsDragOnTop(true);
  };

  const handleDragLeave = () => {
    setIsDragOnTop(false);
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const handleDragDrop = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    setIsDragOnTop(false);

    handleFileSelect(event.dataTransfer.files);
  };

  const handleImageCrop = (croppedArea: Crop, croppedAreaPixels: Crop) => {
    onComplete(file, croppedAreaPixels);
  };

  if (step === 'select') {
    return (
      <UploadArea isDragOnTop={isDragOnTop}>
        <div
          onClick={handleUploadFileClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDragDrop}
        >
          <GetAppIcon />
          <p>
            <FormattedMessage
              id="company.image.uploadInfo"
              defaultMessage="Tipauta kuvatiedosto tähän tai napsauta avataksesi tiedostovalitsimen"
            />
          </p>
          <p>
            <FormattedMessage id="company.image.uploadFormat" defaultMessage=".png / .jpg / .jpeg — max 60 Mb" />
          </p>
          <input type="file" accept="image/png,image/jpeg" onChange={handleUploadFileChange} ref={fileInputRef} />
        </div>
      </UploadArea>
    );
  }

  if (step === 'crop') {
    return (
      <>
        <CropContainer>
          <Cropper
            image={file}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={handleImageCrop}
            onZoomChange={setZoom}
          />
        </CropContainer>
        <Help>
          <FormattedMessage
            id="company.image.cropInfo"
            defaultMessage="Kuvan rajausta voi muokata liikuttamalla kuvaa hiirellä tai sormella, sekä zoomaamalla sisään ja ulos käyttäen hiiren rullaa tai kahta sormea."
          />
        </Help>
      </>
    );
  }
};
